<template>
  <ValidationObserver v-slot="{ passes }">
    <b-modal id="address-form-modal" size="xl" @show="onShow" @hide="onHide">
      <template v-slot:modal-header="{ close }">
        <b-container>
          <div class="modal-title">
            <h3 class="text-bold text-center">{{ form.id ? 'Edit Address' : 'New Address' }}</h3>
            <button class="close" type="button" aria-label="Close" @click="close">
              <i class="di-remove-10"></i>
            </button>
          </div>
        </b-container>
      </template>
      <div>
        <b-form class="container" novalidate="novalidate">
          <h5 class="mb-4">Address Information</h5>
          <section>
            <b-row>
              <b-col cols="12" sm="6">
                <ValidationProvider
                  name="Address Type"
                  rules="required"
                  mode="lazy"
                  v-slot="validationContext"
                >
                  <b-form-group label="Address Type" label-for="type">
                    <b-form-select
                      v-model="form.address.type"
                      :state="getValidationState(validationContext)"
                      name="type"
                    >
                      <b-form-select-option v-for="option in types" :key="option" :value="option">{{
                        option
                      }}</b-form-select-option>
                    </b-form-select>
                    <b-form-invalid-feedback>{{
                      validationContext.errors[0]
                    }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col cols="12" sm="6">
                <b-form-group label="Description (optional)" label-for="description">
                  <b-form-input id="description" v-model="form.address.description"></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
          </section>
          <div class="divider mt-2 mb-4"></div>
          <section>
            <h6 class="text-uppercase mb-3">Contact details</h6>
            <b-row>
              <b-col cols="12" sm="6">
                <ValidationProvider
                  name="First Name"
                  rules="required"
                  mode="lazy"
                  v-slot="validationContext"
                >
                  <b-form-group label="First Name" label-for="first-name">
                    <b-form-input
                      id="first-name"
                      v-model="form.contact.first_name"
                      :state="getValidationState(validationContext)"
                      autofocus=""
                    ></b-form-input>
                    <b-form-invalid-feedback>{{
                      validationContext.errors[0]
                    }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col cols="12" sm="6">
                <ValidationProvider
                  name="Last Name"
                  rules="required"
                  mode="lazy"
                  v-slot="validationContext"
                >
                  <b-form-group label="Last Name" label-for="last-name">
                    <b-form-input
                      id="last-name"
                      v-model="form.contact.last_name"
                      :state="getValidationState(validationContext)"
                    ></b-form-input>
                    <b-form-invalid-feedback>{{
                      validationContext.errors[0]
                    }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12" sm="6">
                <ValidationProvider
                  vid="email"
                  name="Email address"
                  rules="required|email"
                  mode="lazy"
                  v-slot="validationContext"
                >
                  <b-form-group label="E-mail address" label-for="email">
                    <b-form-input
                      id="email"
                      v-model="form.contact.email"
                      :state="getValidationState(validationContext)"
                    ></b-form-input>
                    <b-form-invalid-feedback>{{
                      validationContext.errors[0]
                    }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col cols="12" sm="6">
                <ValidationProvider
                  name="Mobile"
                  rules="required"
                  mode="lazy"
                  v-slot="validationContext"
                >
                  <b-form-group label="Mobile" label-for="mobile">
                    <b-form-input 
                      id="mobile" 
                      v-model="form.contact.mobile"
                      :state="getValidationState(validationContext)"
                    ></b-form-input>
                    <b-form-invalid-feedback>{{
                      validationContext.errors[0]
                    }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
            </b-row>
          </section>
          <div class="divider mt-2 mb-4"></div>
          <section>
            <h6 class="text-uppercase mb-3">Address Details</h6>
            <b-row>
              <b-col cols="12" sm="6">
                <ValidationProvider
                  name="Country"
                  rules="required"
                  mode="lazy"
                  v-slot="validationContext"
                >
                  <b-form-group label="Country" label-for="country">
                    <b-form-select
                      id="country"
                      v-model="form.address.country"
                      :options="countries"
                      value-field="abbreviation"
                      text-field="country"
                      :state="getValidationState(validationContext)"
                    ></b-form-select>
                    <b-form-invalid-feedback>{{
                      validationContext.errors[0]
                    }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <ValidationProvider
                  name="Street Address"
                  rules="required"
                  mode="lazy"
                  v-slot="validationContext"
                >
                  <b-form-group label="Street Address" label-for="address1">
                    <vue-google-autocomplete
                      id="address1"
                      ref="addressInput"
                      v-model="form.address.address1"
                      :country="form.address.country?.toLowerCase()"
                      :enable-geolocation="false"
                      :fields="['address_components', 'formatted_address', 'geometry']"
                      types="address"
                      placeholder="Start typing your address"
                      :class="[
                        'form-control',
                        { 'is-invalid': getValidationState(validationContext) === false },
                      ]"
                      autocomplete="off"
                      @change="handleAddressInput"
                      v-on:placechanged="onPlaceChanged"
                      @input="handleAddressInput"
                      :componentRestrictions="{
                        country: form.address.country?.toLowerCase(),
                        types: ['address', 'sublocality', 'sublocality_level_1'],
                      }"
                    />
                    <b-form-invalid-feedback>{{
                      validationContext.errors[0]
                    }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-form-group label="Address 2 (optional)" label-for="address2">
                  <b-form-input
                    id="address2"
                    v-model="form.address.address2"
                    placeholder="Apartment, suite, unit, etc. (optional)"
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Dynamic address fields based on country format -->
            <template v-for="(field, index) in addressFieldsForCountry">
              <b-row :key="field.name">
                <b-col cols="12" :sm="addressFieldsForCountry.length % 2 !== 0 && index === addressFieldsForCountry.length - 1 ? 12 : 6">
                  <ValidationProvider
                    :name="field.label"
                    :rules="getValidationRules(field)"
                    mode="lazy"
                    v-slot="validationContext"
                  >
                    <b-form-group :label="field.label" :label-for="field.name">
                      <b-form-input
                        :id="field.name"
                        v-model="form.address[field.name]"
                        :state="getValidationState(validationContext)"
                        :maxlength="getMaxLength(field.name)"
                      ></b-form-input>
                      <b-form-invalid-feedback>{{
                        validationContext.errors[0]
                      }}</b-form-invalid-feedback>
                    </b-form-group>
                  </ValidationProvider>
                </b-col>
              </b-row>
            </template>
          </section>
        </b-form>
      </div>
      <template v-slot:modal-footer="{ cancel }">
        <div class="flex-fill m-0">
          <div class="container">
            <div class="d-flex">
              <b-button class="mr-3" variant="outline-light" @click="cancel">Cancel</b-button>
              <b-button
                class="mr-3"
                variant="primary"
                type="submit"
                @click="passes(confirm)"
                :disabled="isLoading"
              >
                <b-spinner v-if="isLoading" small=""></b-spinner> Confirm
              </b-button>
            </div>
          </div>
        </div>
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import VueGoogleAutocomplete from 'vue-google-autocomplete';
import countries from '@/data/countries';
import apiAddresses from '@/api/addresses';

export default {
  name: 'AddressForm',
  components: {
    ValidationProvider,
    ValidationObserver,
    VueGoogleAutocomplete,
  },
  data() {
    return {
      countries,
      types: ['Physical', 'Postal', 'Home', 'Work', 'Billing', 'Shipping', 'Other'],
      isLoading: false,
      isPopulating: false,
      errors: null,
      form: {
        id: undefined,
        contact: {
          first_name: undefined,
          last_name: undefined,
          email: undefined,
          mobile: undefined,
        },
        address: {
          type: undefined,
          description: undefined,
          address1: undefined,
          address2: undefined,
          suburb: undefined,
          city: undefined,
          state: undefined,
          postcode: undefined,
          country: undefined,
        },
      },
      addressFieldsConfig: [
        {
          addressFormat: 'United States',
          fields: [
            {
              name: 'city',
              label: 'City',
              mapType: ['administrative_area_level_2', 'locality', 'sublocality_level_1', 'postal_town'],
            },
            {
              name: 'state',
              label: 'State',
              mapType: ['administrative_area_level_1'],
            },
            {
              name: 'postcode',
              label: 'ZIP Code',
              mapType: ['postal_code'],
            },
          ],
        },
        {
          addressFormat: 'United Kingdom',
          fields: [
            {
              name: 'suburb',
              label: 'Street',
              mapType: ['route'],
            },
            {
              name: 'city',
              label: 'City/Town',
              mapType: ['administrative_area_level_2', 'locality'],
            },
            {
              name: 'state',
              label: 'State',
              optional: true,
              mapType: ['administrative_area_level_1'],
            },
            {
              name: 'postcode',
              label: 'Postcode',
              mapType: ['postal_code'],
            },
          ],
        },
        {
          addressFormat: 'Canada',
          fields: [
            {
              name: 'city',
              label: 'City',
              mapType: ['administrative_area_level_2', 'locality'],
            },
            {
              name: 'state',
              label: 'State',
              mapType: ['administrative_area_level_1'],
            },
            {
              name: 'postcode',
              label: 'Postal Code',
              mapType: ['postal_code'],
            },
          ],
        },
        {
          addressFormat: 'Australia',
          fields: [
            {
              name: 'suburb',
              label: 'Suburb',
              mapType: ['locality'],
            },
            {
              name: 'postcode',
              label: 'Postcode',
              mapType: ['postal_code'],
            },
            {
              name: 'state',
              label: 'State',
              mapType: ['administrative_area_level_1'],
            },
          ],
        },
        {
          addressFormat: 'New Zealand',
          fields: [
            {
              name: 'city',
              label: 'City/Town',
              mapType: ['administrative_area_level_2', 'sublocality_level_1'],
            },
            {
              name: 'state',
              label: 'State',
              mapType: ['administrative_area_level_1'],
            },
            {
              name: 'suburb',
              label: 'Suburb',
              optional: true,
              mapType: ['locality', 'sublocality'],
            },
            {
              name: 'postcode',
              label: 'Postcode',
              mapType: ['postal_code'],
            },
          ],
        },
        {
          addressFormat: 'Singapore',
          countryCode: 'SG',
          fields: [
            {
              name: 'city',
              label: 'City',
              mapType: ['locality', 'administrative_area_level_2'],
            },
            {
              name: 'postcode',
              label: 'ZIP Code',
              mapType: ['postal_code'],
            },
          ],
        },
      ],
    };
  },
  computed: {
    ...mapState({
      addressFormData: state => state.addressModal.newAddressFormData,
      addressFormCallback: state => state.addressModal.addressFormCallback,
    }),
    ...mapGetters('auth', ['currentRegion']),
    contact() {
      return {
        first_name: this._.get(this.addressFormData, 'first_name'),
        last_name: this._.get(this.addressFormData, 'last_name'),
        email: this._.get(this.addressFormData, 'email'),
        mobile: this._.get(this.addressFormData, 'mobile'),
      };
    },
    address() {
      return this._.get(this.addressFormData, 'address');
    },
    customer() {
      return this._.get(this.addressFormData, 'customer');
    },
    isDynamic() {
      return this._.get(this.addressFormData, 'is_dynamic');
    },
    isAustralianAddress() {
      return this.form.address.country === 'AU';
    },
    stateRules() {
      if (this.isAustralianAddress) {
        return 'required|au_state';
      }
      return 'required';
    },
    suburbRules() {
      if (this.isAustralianAddress) {
        return 'alpha_spaces';
      }
      return '';
    },
    postcodeRules() {
      if (this.isAustralianAddress) {
        return { required: true, au_postcode: { state: this.form.address.state } };
      }
      if (this.form.address.country === 'CA') {
        return 'required|ca_postcode';
      }
      return 'required';
    },
    addressFieldsForCountry() {
      const countryCode = this.form.address.country;
      let format;

      switch (countryCode) {
        case 'US':
          format = 'United States';
          break;
        case 'GB':
          format = 'United Kingdom';
          break;
        case 'CA':
          format = 'Canada';
          break;
        case 'AU':
          format = 'Australia';
          break;
        case 'NZ':
          format = 'New Zealand';
          break;
        case 'SG':
          format = 'Singapore';
          break;
        default:
          format = 'United States'; // Default format
      }

      const countryFormat = this.addressFieldsConfig.find(
        config => config.addressFormat === format,
      );

      return countryFormat ? countryFormat.fields : [];
    },
  },
  methods: {
    ...mapMutations('addressModal', {
      setShippingAddress: 'SET_SHIPPING_ADDRESS',
      setSelectedBillingAddressId: 'SET_SELECTED_BILLING_ADDRESS_ID',
    }),
    onShow() {
      this.populateData();
      this.$nextTick(() => {
        this.initializeAutocomplete();
      });
    },
    onHide() {
      this.$store.commit('addressModal/HIDE_NEW_ADDRESS_FORM');
    },
    confirm() {
      this.isLoading = true;
      this.errors = null;
      if (this.form.id) {
        if (this.isDynamic) {
          apiAddresses
            .postBillingAddress({
              ...this.form,
              customer_id: this._.get(this.customer, 'id'),
            })
            .then(this.onSuccess)
            .catch(this.onError);
        } else {
          apiAddresses
            .putBillingAddress({
              ...this.form.contact,
              address: this.form.address,
              id: this.form.id,
            })
            .then(this.onSuccess)
            .catch(this.onError);
        }
      } else if (this.customer) {
        apiAddresses
          .postBillingAddress({
            ...this.form,
            customer_id: this._.get(this.customer, 'id'),
          })
          .then(this.onSuccess)
          .catch(this.onError);
      } else {
        this.$store.dispatch('showErrorAlert', 'New address cannot be added to guests.');
      }
    },
    populateData() {
      this.isLoading = false;
      this.isPopulating = true;
      this.errors = null;
      const id = this._.get(this.addressFormData, 'id');
      this.form = {
        id,
        contact: id
          ? { ...this.contact }
          : {
              ...this.contact,
              ...this._.pick(this.customer, Object.keys(this.form.contact)),
            },
        address: {
          type: 'Shipping',
          ...this.address,
          country: this.address?.country || (this.currentRegion.country_code.toLowerCase() == 'int' ? 'US' : this.currentRegion.country_code),
        },
      };
      this.$nextTick(() => {
        this.isPopulating = false;
      });
    },
    onSuccess(response) {
      this.setShippingAddress(response.data.data);
      this.setSelectedBillingAddressId(response.data.data.id);
      if (this.addressFormCallback) {
        this.addressFormCallback(response);
      }

      this.isLoading = false;
      this.hideAddAddressModal();
      this.$store.dispatch('showSuccessAlert', 'New address has been successfully created!');
    },
    onError({ response }) {
      const message = this._.get(response, 'data.message');
      this.$store.dispatch('showErrorAlert', message);
      this.isLoading = false;
    },
    initializeAutocomplete() {
      if (this.$refs.addressInput) {
        if (this.form.address.address1) {
          this.$refs.addressInput.update(this.form.address.address1);
        } else {
          this.$refs.addressInput.clear();
        }
      }
    },
    onPlaceChanged(place) {
      try {
        console.log('Place data received:', place);

        if (!place) {
          console.warn('Place data is null or undefined');
          return;
        }

        // Reset address fields before filling with new data
        this.resetAddressFields();

        if (place.latitude && place.longitude) {
          // Try to get full address data using geocoder
          const geocoder = new google.maps.Geocoder();
          const latlng = new google.maps.LatLng(place.latitude, place.longitude);

          geocoder.geocode({ location: latlng }, (results, status) => {
            if (status === 'OK') {
              // Map the address components
              const formattedAddress = this.$refs.addressInput.$attrs.value;
              const checkAddress = results.find(result => result.formatted_address.split(' ')[0].toLowerCase() == formattedAddress.split(' ')[0].toLowerCase());
              if (checkAddress) {
                this.mapAddressComponents(checkAddress,formattedAddress);
              } else {
                this.mapAddressComponents(results[0],formattedAddress);
              }
            }
          });
        } else if (place.address_components) {
          // If we have address components directly, use them
          // Create a proper place object structure for mapAddressComponents
          const placeObject = {
            address_components: place.address_components,
            formatted_address: place.formatted_address
          };
          this.mapAddressComponents(placeObject,place.formatted_address);
        } else {
          // Fallback: just set the formatted address
          this.$set(this.form.address, 'address1', place.formatted_address);
          
          // Update the vue-google-autocomplete component's internal value
          this.$nextTick(() => {
            if (this.$refs.addressInput) {
              this.$refs.addressInput.update(place.formatted_address);
            }
          });
        }
      } catch (error) {
        console.error('Error processing place data:', error);
        console.error('Place object:', place);
      }
    },
    resetAddressFields() {
      const fieldsToReset = ['address2', 'suburb', 'city', 'state', 'postcode'];
      fieldsToReset.forEach(field => {
        this.$set(this.form.address, field, '');
      });
    },
    mapAddressComponents(components,formattedAddress) {
      if (!components || components.address_components.length === 0) {
        console.warn('No address components to map');
        return;
      }
      console.log(components.address_components);
      // Get the address format configuration for the current country
      const countryFormat = this.addressFieldsConfig.find(
        config =>
          config.addressFormat === this.getAddressFormatForCountry(this.form.address.country),
      );

      if (!countryFormat) {
        console.warn('No address format found for country:', this.form.address.country);
        return;
      }

      // Process each field defined in the address format
      countryFormat.fields.forEach(field => {
        // Find the component that matches any of the mapType values
        const addressComponent = components.address_components.find(component => {
          return field.mapType.some(type => component.types.includes(type));
        });

        if (addressComponent) {
          // Determine which name type to use (short or long)
          const nameType =
            field.name === 'state' || field.mapType.includes('administrative_area_level_1')
              ? 'short_name'
              : 'long_name';

          // Use Vue.set to ensure reactivity
          this.$set(this.form.address, field.name, addressComponent[nameType]);
        }
      });

      // Handle specific country formats
      if (this.form.address.country === 'AU') {
        const localityComponent = components.address_components.find(component =>
          component.types.includes('locality'),
        );
        if (localityComponent) {
          this.$set(this.form.address, 'suburb', localityComponent.long_name);
        }
      }

      // Process formatted address for address1 field
        let formatted_address = formattedAddress.split(',')[0];

        this.$set(this.form.address, 'address1', formatted_address);
        console.log('formatted_address', components);
        // Update the vue-google-autocomplete component's internal value
        this.$nextTick(() => {
          if (this.$refs.addressInput) {
            this.$refs.addressInput.update(formatted_address);
          }
        });
      // Force a re-render after all updates
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    getAddressFormatForCountry(countryCode) {
      switch (countryCode) {
        case 'US':
          return 'United States';
        case 'GB':
          return 'United Kingdom';
        case 'CA':
          return 'Canada';
        case 'AU':
          return 'Australia';
        case 'NZ':
          return 'New Zealand';
        case 'SG':
          return 'Singapore';
        default:
          return 'United States'; // Default format
      }
    },
    handleAddressInput(value) {
      if (this.$refs.addressInput) {
        this.$refs.addressInput.update(value);
      }
      this.$set(this.form.address, 'address1', value);
      console.log('update', this.form.address);
    },
    getValidationRules(field) {
      if (field.optional) {
        return '';
      }

      if (this.isAustralianAddress && field.name === 'state') {
        return this.stateRules;
      }
      if (field.name === 'postcode') {
        return this.postcodeRules;
      }

      return 'required';
    },
    getMaxLength(fieldName) {
      // Set character limits for Australian address fields
      if (this.isAustralianAddress) {
        if (fieldName === 'state') {
          return 3;
        }
        if (fieldName === 'postcode') {
          return 4;
        }
      }
      if (this.form.address.country === 'CA' && fieldName === 'postcode') {
        return 7;
      }
      return null; // No limit for other fields
    },
  },
  watch: {
    'form.address.country'(newVal) {
      if (this.isPopulating) {
        return;
      }
      if (newVal) {
        this.resetAddressFields();
        this.$nextTick(() => {
          this.initializeAutocomplete();
        });
      }
    },
  },
};
</script>

<style>
/* Remove scoped to allow these styles to affect elements outside the component */
.pac-container {
  z-index: 10000 !important; /* Keep high z-index to stay above modal */
  position: absolute !important; /* Change to absolute for proper positioning */
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  max-height: 200px !important; /* Add max height */
  overflow-y: auto !important; /* Make it scrollable */
  overflow-x: hidden !important;
}

.pac-item {
  cursor: pointer;
  padding: 8px;
}

.pac-item:hover {
  background-color: #f5f5f5;
}

/* Ensure modal doesn't hide autocomplete dropdown */
.modal-dialog {
  z-index: 1050;
}
</style>
